import { useMemo, useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  useParams,
  useLocation,
  useNavigate,
  unstable_useBlocker as useBlocker,
} from "react-router";

import VLWizard from "views/uikit/VLWizard";
import { WizardStep } from "views/uikit/VLWizard";
import CheckIcon from "assets/icons/CheckIcon";
import AddDataContent from "./Steps/AddDataContent";
import PreviewContent from "./Steps/PreviewContent";
import CreateDataset from "./Steps/CreateDataset";
import useDatasetWizardNavigation from "./useButtonConfigs";

import { FolderMetadata, FolderUploadStatus } from "types";
import {
  getFoldersWithLocalSelections,
  getFolderUploadStatus,
  getUploadingFolder,
  getMultiFolderUpload,
  getInvalidFolders,
} from "redux/CreateDataset/selectors";
import {
  fetchFolderMetadata,
  setLocalFolderSelections,
  startCreationStatusPolling,
  stopCreationStatusPolling,
} from "redux/CreateDataset/actions";
import abortControllerManager from "helpers/abortControllerManager";

import styles from "./style.module.scss";

const uploadingStatusMap: Record<FolderUploadStatus, boolean> = {
  [FolderUploadStatus.ACTIVE]: true,
  [FolderUploadStatus.COMPLETED]: false,
  [FolderUploadStatus.IDLE]: false,
  [FolderUploadStatus.FAILED]: false,
};

const getStepStatus = (currentStep: number, step: number) => {
  if (currentStep === step) return "process";
  if (currentStep > step) return "finish";
  return "wait";
};

const renderIcon = (status: string): JSX.Element | null => {
  if (status === "finish")
    return (
      <div className={styles.checkIconWrapper}>
        <CheckIcon variant="flat" />
      </div>
    );
  return null;
};

// function to determine initial step based on status parameter
const getInitialStepFromStatus = (status: string | null): number => {
  const statusToStep: Record<string, number> = {
    uploading: 0,
    indexing: 2,
    new: 0,
  };

  return statusToStep[status || ""] ?? 0;
};

const DatasetCreationWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [uploadedFolderNames, setUploadedFolderNames] = useState<string[]>([]);

  // Get status parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const statusParam = searchParams.get("status");

  const [currentStep, setCurrentStep] = useState<number>(() =>
    getInitialStepFromStatus(statusParam)
  );

  // Handle status-based navigation with history replacement
  useEffect(() => {
    if (statusParam && newDatasetId) {
      if (statusParam === "new") {
        // Redirect to data page for "new" status
        navigate(`/dataset/${newDatasetId}/data`, { replace: true });
        return;
      }

      // For other status values, remove status parameter from URL to avoid repeated calls on back navigation
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("status");
      const newSearch = newSearchParams.toString();
      const newUrl = `/dataset/${newDatasetId}/create${newSearch ? `?${newSearch}` : ""}`;

      // Replace current history entry to prevent back navigation to create dataset
      navigate(newUrl, { replace: true });
    }
  }, [statusParam, newDatasetId, location.search, navigate]);

  // Separate effect for creation status polling to avoid interference
  useEffect(() => {
    if (!newDatasetId) return;

    // Step 2 (CreateDataset): Poll creation status when on this page
    if (currentStep === 2) {
      dispatch(startCreationStatusPolling(newDatasetId) as any);
    } else {
      dispatch(stopCreationStatusPolling() as any);
    }

    // Cleanup on unmount
    return () => {
      dispatch(stopCreationStatusPolling() as any);
    };
  }, [currentStep, newDatasetId, dispatch]);

  useEffect(() => {
    if (currentStep === 0 && newDatasetId) {
      setIsTableLoading(true);
      dispatch(fetchFolderMetadata(newDatasetId) as any).finally(() =>
        setIsTableLoading(false)
      );
    }
  }, [currentStep, newDatasetId, dispatch]);

  const invalidFolders = useSelector(getInvalidFolders);
  const uploadingFolder = useSelector(getUploadingFolder);
  const folders = useSelector(getFoldersWithLocalSelections);
  const multiFolderUpload = useSelector(getMultiFolderUpload);
  const folderUploadStatus = useSelector(getFolderUploadStatus);

  const selectedFolderIds = useMemo(() => {
    return folders.filter((f) => f.selected).map((f) => f.id);
  }, [folders]);

  // Clean up uploaded folder names when they appear in metadata OR when they are invalid
  useEffect(() => {
    if (uploadedFolderNames.length > 0) {
      const existingFolderNames = folders.map((f) => f.folder_name);
      const invalidFolderNames = invalidFolders.map((f) => f.folder_name);

      // Remove folder names that are either in metadata or marked as invalid
      const stillPendingFolders = uploadedFolderNames.filter(
        (folderName) =>
          !existingFolderNames.includes(folderName) &&
          !invalidFolderNames.includes(folderName)
      );

      if (stillPendingFolders.length !== uploadedFolderNames.length) {
        setUploadedFolderNames(stillPendingFolders);
      }
    }
  }, [folders, invalidFolders, uploadedFolderNames]);

  const showUploadLoader = useMemo(() => {
    return uploadedFolderNames.length > 0;
  }, [uploadedFolderNames]);

  // Comprehensive upload detection
  const isUploadingActive = useMemo(() => {
    // Check if single folder upload is in progress
    const singleFolderUpload =
      uploadingStatusMap[folderUploadStatus] || uploadingFolder !== null;

    // Check if multi-folder upload is in progress
    const multiFolderUploadActive =
      multiFolderUpload !== null &&
      multiFolderUpload.status === FolderUploadStatus.ACTIVE;

    return singleFolderUpload || multiFolderUploadActive;
  }, [folderUploadStatus, uploadingFolder, multiFolderUpload]);

  // Add navigation guard when folder upload is active
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Show confirmation dialog when user tries to leave during upload
      const message =
        "Upload is in progress. Are you sure you want to leave? This will cancel the upload.";
      event.preventDefault();
      event.returnValue = message; // For older browsers
      return message;
    };

    if (isUploadingActive) {
      // Add beforeunload listener when upload is active
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUploadingActive]);

  // Block React Router navigation when upload is active
  const blocker = useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: any;
      nextLocation: any;
    }) => {
      // Block navigation if upload is in progress and trying to navigate away from current page
      return (
        isUploadingActive && currentLocation.pathname !== nextLocation.pathname
      );
    }
  );

  // Handle the blocked navigation with confirmation
  useEffect(() => {
    if (blocker.state === "blocked") {
      const proceed = window.confirm(
        "Upload is in progress. Are you sure you want to leave? This will cancel the upload."
      );

      if (proceed) {
        // User confirmed - abort the upload and proceed with navigation
        abortControllerManager.abortController();
        blocker.proceed();
      } else {
        // User cancelled - reset the blocker
        blocker.reset();
      }
    }
  }, [blocker]);

  const handleFolderChange = useCallback(
    (updatedFolders: FolderMetadata[]) => {
      // Only consider valid folders for selection (exclude invalid folders)
      const validFolders = updatedFolders.filter((f) => f.status !== "invalid");
      const selectedIds = validFolders
        .filter((f) => f.selected)
        .map((f) => f.id);
      dispatch(setLocalFolderSelections(selectedIds));
    },
    [dispatch]
  );

  const handleFolderUploadStart = useCallback((folderNames: string[]) => {
    setUploadedFolderNames(folderNames);
  }, []);

  const buttonsConfig = useDatasetWizardNavigation({
    currentStep,
    selectedFolderIds,
    isFolderUploading: isUploadingActive,
    setCurrentStep,
  });

  const steps: WizardStep[] = useMemo(
    () => [
      {
        title: "Add Data",
        status: getStepStatus(currentStep, 0),
        icon: renderIcon(getStepStatus(currentStep, 0)),
        disabled: false,
        content: (
          <AddDataContent
            folders={folders}
            isLoading={showUploadLoader}
            isTableLoading={isTableLoading}
            onFoldersChange={handleFolderChange}
            onFolderUploadStart={handleFolderUploadStart}
          />
        ),
      },
      {
        title: "Preview",
        status: getStepStatus(currentStep, 1),
        icon: renderIcon(getStepStatus(currentStep, 1)),
        disabled: true,
        content: <PreviewContent />,
      },
      {
        title: "Create Dataset",
        status: getStepStatus(currentStep, 2),
        icon: renderIcon(getStepStatus(currentStep, 2)),
        disabled: true,
        content: <CreateDataset />,
      },
    ],
    [
      currentStep,
      folders,
      showUploadLoader,
      isTableLoading,
      handleFolderChange,
      handleFolderUploadStart,
    ]
  );

  return (
    <VLWizard
      steps={steps}
      current={currentStep}
      buttonsConfig={buttonsConfig}
    />
  );
};

export default DatasetCreationWizard;
